import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import { selectViewingRecord, setViewingRecord } from "@/state/redux/slices/ui";
import { serializeRecord } from "@/common/record";
import { formatDate } from "@/common/utils";
import { HGRecord } from "@/common/types";
import { NotesSortField, SortOrder } from "@/state/recordsView";
import RecordBody, { recordCardClass } from "./RecordBody";

interface RecordsNotesProps {
  records: HGRecord[];
  sort: NotesSortField;
  order: SortOrder;
  /** The collection being browsed, so its badge can be left off every card. */
  collectionID?: string;
  /** Shown when `records` is empty; lets the caller distinguish "no notes at
   * all" from "no notes match the current search". */
  emptyMessage?: string;
}

function timestamp(record: HGRecord, field: NotesSortField) {
  if (field === "modified") return record.modified ?? record.created;
  return record.created;
}

/**
 * Note records have no place on the time axis, so they get no axis: a plain
 * card list ordered by when they were written rather than by what they are
 * about.
 */
export default function RecordsNotes({
  records,
  sort,
  order,
  collectionID,
  emptyMessage = "No notes in this collection",
}: RecordsNotesProps) {
  const dispatch = useAppDispatch();
  const viewingRecord = useAppSelector(selectViewingRecord);

  const sorted = useMemo(() => {
    const direction = order === "asc" ? 1 : -1;
    return [...records].sort(
      (a, b) => direction * (timestamp(a, sort) - timestamp(b, sort)),
    );
  }, [records, sort, order]);

  if (sorted.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 px-4 py-3">
      {sorted.map((record) => {
        const selected = !!(viewingRecord && record.id === viewingRecord.id);
        return (
          <div
            key={record.id}
            onClick={() =>
              dispatch(
                selected ? setViewingRecord(null) : setViewingRecord(serializeRecord(record)),
              )
            }
            className={recordCardClass(selected)}
          >
            <RecordBody
              record={record}
              currentCollectionID={collectionID}
              meta={
                <span className="font-mono text-[10.5px] text-muted-foreground">
                  {sort === "modified" && record.modified ? "Modified " : "Created "}
                  {formatDate(timestamp(record, sort), "short")}
                </span>
              }
            />
          </div>
        );
      })}
    </div>
  );
}
